/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Box, Stack } from '@mui/system';
import { Dialog, Typography, IconButton } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { DetachPatientModal } from 'shared/modals/DetachPatientModal/DetachPatientModal';
import { HistoryActionsModal } from 'shared/modals/HistoryActionsModal/HistoryActionsModal';
import { EditPatientModal } from 'shared/modals/EditPatientModal/EditPatientModal';
import { TooltipActionsInfo, TooltipActionsWarning } from 'ui/tooltips/tooltipAction';
import { useCompanyActions } from 'hooks/companies/useCompanyActions';
import { RelativeSlotsModal } from 'shared/modals/RelativeSlotsModal';

import { Text } from '@bestdoctor/spectrum-uikit/Text';
import { Button } from '@bestdoctor/spectrum-uikit/Button';
import { Divider } from '@bestdoctor/spectrum-uikit/Divider';

import { modalTypographyColor, modalBlockRowStyle, modalBlockFooterStyle } from 'ui/modals/modalGlobal';

import { InfoPatientModalProps, IEventTypes } from 'interfaces/patients/patientInterfaces';
import { useSnackbar } from 'notistack';
import { ErrorData } from 'interfaces/global/globalInterfaces';
import { useLoadClinicsList } from 'hooks/programs/useLoadClinicsList';
import { useGetPeriodStaffAction } from 'hooks/periods/useGetPeriodStaffAction';
import { theme } from 'ui/theme/theme';
import { getFormattedDate } from 'utils/dateHelpers';
import { useGetRelativePatients } from 'hooks/patients/useGetRelativePatients';
import { SearchRelativePatient } from 'interfaces/hooks/patientsHooksInterfaces';
import { useSendWelcomeLetter } from 'hooks/patients/useSendWelcomeLetter';
import { isAccessAllowed } from 'utils/isAccessAllowed';
import { themeColor } from 'utils/uiConstants';
import { useSearchPrograms } from 'hooks/programs/useSearchProgram';
import { mapProgramsToSelectOptions } from 'utils/mapProgramsToSelectOptions';
import { useForceAttachPatient } from 'hooks/patients/useForceAttachPatient';
import { useCancelPatientAttach } from 'hooks/patients/useCancelPatientAttach';
import { useGetProposalDetachmentAction } from 'hooks/patients/useGetProposalDetachmentAction';
import { useCancelDetachment } from 'hooks/patients/useCancelDetachment';
import { isActive } from 'itson/ItsOn';
import { handleRefreshToken } from 'utils/refreshToken';
import { HEADER_ACCEPT_HR } from 'utils/apiConstants';
import { getCompanyName } from 'utils/companyName';
import { analyticsTracker } from 'utils/analytics';
import { getDetachTooltipTitle } from './getDetachTooltipTitle';
import { ChangeProgramModal } from '../EditPatientModal/ChangeProgramModal';
import { EarlyAttachWarning } from '../EarlyAttachWarning/EarlyAttachWarning';
import { CancelAttachWarning } from '../CancelAttachWarning/CancelAttachWarning';
import { EditProposalModal } from '../EditProposalModal/EditProposalModal';

const LuchiIcon: React.FC<{ sx?: React.CSSProperties }> = ({ sx }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={sx}>
    <path
      d="M8.60512 5.38471L8.42392 1.3335H7.5742L7.393 5.38471L6.95526 5.53635L4.43088 2.36156L3.7347 2.87559L5.95676 6.23158C6.64055 7.25964 8.66616 6.67027 8.60512 5.38471Z"
      fill="#5C777D"
    />
    <path
      d="M10.6765 7.77368L14.4559 6.3527L14.1985 5.53635L10.3132 6.6245L10.0271 6.24303L12.2472 2.84508L11.5797 2.36156L9.05527 5.52109C8.31808 6.45856 9.452 8.24193 10.6765 7.77368Z"
      fill="#5C777D"
    />
    <path
      d="M5.97492 9.78401L3.72329 13.14L4.41852 13.654L6.9429 10.4792L7.3959 10.6299L7.57709 14.6668H8.42396L8.60516 10.6299C8.66619 9.33006 6.68541 8.71017 5.97492 9.78401Z"
      fill="#5C777D"
    />
    <path
      d="M5.68784 6.62448L1.78825 5.53633L1.53076 6.35268L5.32544 7.77366L5.31018 8.24191L1.53076 9.64763L1.78825 10.464L5.7031 9.3911C6.94288 9.05826 6.92762 6.97257 5.68784 6.62448Z"
      fill="#5C777D"
    />
    <path
      d="M9.05911 10.4945L11.5835 13.6417L12.2787 13.1276L10.0261 9.77164L10.3122 9.39017L14.2128 10.4631L14.4693 9.64671L10.6756 8.24099C9.45202 7.78895 8.24181 9.46455 9.05911 10.4945Z"
      fill="#5C777D"
    />
  </svg>
);

const ExternalLinkIcon: React.FC<{ sx?: React.CSSProperties }> = ({ sx }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={sx}>
    <path
      d="M3.33366 4.3335L11.667 3.3335L12.667 4.3335L11.667 12.6668H10.667L10.6695 6.66409L4.00033 12.6668L2.66699 11.3335L9.33366 5.3335H3.33366V4.3335Z"
      fill="#5C777D"
    />
  </svg>
);

export const InfoStaffModal: React.FC<InfoPatientModalProps> = ({
  open,
  selectedCompanies,
  patientData,
  isErrorPatient,

  handleClose,
  refetchPatient,
  isAddingForbiddenByDates,
  isDetachingForbiddenByDates,
  accessList,
  rolesData,
  defaultPermissions,
  isSlotsAviable,
  refetchSearchPatients,
}) => {
  const [isDetachPatientModal, setDetachPatientModal] = useState<boolean>(false);
  const [isEditDetachModal, setEditDetachModal] = useState<boolean>(false);
  const [isHistoryActionsModal, setHistoryActionsModal] = useState<boolean>(false);
  const [isEditPatientModal, setEditPatientModal] = useState(false);
  const [slotsModalOpen, setSlotsModalOpen] = useState<boolean>(false);
  const [slotRelative, setSlotRelative] = useState<SearchRelativePatient | null>(null);
  const [isEarlyAttachWarning, setEarlyAttachWarning] = useState<boolean>(false);
  const [isCancelAttachWarning, setCancelAttachWarning] = useState<boolean>(false);
  const [isEditProposalModal, setEditProposalModal] = useState<boolean>(false);
  const [isChangeProgramModal, setIsChangeProgramModal] = useState<boolean>(false);

  // стейты для модалки истории операций
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [eventType, setEventType] = useState<IEventTypes[]>([]);
  const [orderingTableHistory, setOrderingTableHistory] = useState<string>('');

  const { enqueueSnackbar } = useSnackbar();

  const mainFilterPeriodStaff =
    'mainFilterPeriodStaff' in sessionStorage ? JSON.parse(sessionStorage.getItem('mainFilterPeriodStaff') || '') : '';

  const patientCompany = useMemo(
    () => selectedCompanies.find((company) => company.uuid === patientData?.companyUuid) ?? null,
    [selectedCompanies, patientData?.companyUuid]
  );

  const { periodStaffAction } = useGetPeriodStaffAction({
    period: mainFilterPeriodStaff,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });

  const { handleCancelDetachment } = useCancelDetachment({
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });
  const { proposalDetachmentActionData } = useGetProposalDetachmentAction({
    uuid: patientData?.uuid,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
    enabled: isActive('hr-591.enable_detachment_proposal_action_by_slot'),
  });

  const {
    CompanyActionsData,
    isLoadingCompanyActions,
    hasNextPageCompanyActions,
    isFetchingNextPageCompanyActions,
    fetchNextPageCompanyActions,
    refetchCompanyActions,
  } = useCompanyActions({
    isSlotsAviable,
    companies: selectedCompanies,

    startDate,
    endDate,
    eventType,
    ordering: orderingTableHistory,

    patient: patientData?.patientUuid || '',
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const { programs: searchPrograms } = useSearchPrograms({
    company: patientCompany || null,
    period: mainFilterPeriodStaff,
    selectedProgram: null,
    selectedServices: [],
    selectedСity: null,
    fetchAllPages: true,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const programs = useMemo(
    () => mapProgramsToSelectOptions(searchPrograms, { withBudget: true }),
    [searchPrograms]
  );
  const patientProgram = useMemo(() => {
    const fromList = programs.find((program) => program.uuid === patientData?.program_uuid);
    if (fromList) {
      return fromList;
    }
    if (!patientData?.program_uuid) {
      return null;
    }
    return {
      id: patientData.program_uuid,
      uuid: patientData.program_uuid,
      title: patientData.program_title,
    };
  }, [programs, patientData?.program_uuid, patientData?.program_title]);

  const { searchRelativePatientsData } = useGetRelativePatients({
    patient: patientData,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const { handleLoadClinicsList } = useLoadClinicsList({
    programUuid: patientData?.program_uuid,
    programName: patientData?.program_uuid,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const { handleForceAddPatient } = useForceAttachPatient({
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
    onSuccess: () => {
      setEarlyAttachWarning(false);
      enqueueSnackbar('Запрос на активацию направлен, полис активируется в ближайшее время', { variant: 'success' });
    },
  });

  const { handleCancelPatientAttach } = useCancelPatientAttach({
    onSuccess: () => {
      setCancelAttachWarning(false);
      enqueueSnackbar('Запрос на отмену направлен', { variant: 'success' });
    },
  });

  const { handleSendWelcomeLetter } = useSendWelcomeLetter({
    slotUuid: patientData?.uuid,
    onSuccess: () => {
      enqueueSnackbar('Письмо отправлено!', { variant: 'success' });
      analyticsTracker.trackPatientCardSendWelcome();
      refetchPatient();
    },
    onError: () => {
      enqueueSnackbar('При отправке письма произошла ошибка', { variant: 'error' });
    },
  });

  const isPatientTypeSlot: boolean = patientData?.content_type === 'slot' || !isActive('hr-507.cross_actions');

  const isPatientTypeProposalDetach: boolean =
    proposalDetachmentActionData?.data?.base_action?.proposal_type === 'patient_detachment';

  const handleEditButtonClick = React.useCallback(() => {
    if (isPatientTypeSlot) {
      setEditPatientModal(true);
    } else {
      setEditProposalModal(true);
    }
  }, [isPatientTypeSlot]);

  const tooltipTitle = React.useMemo(() => {
    let title = '';
    if (patientData?.type === 'Завершенный') {
      title = 'Нельзя менять программу у завершенного пациента';
    }
    if (patientData?.scheduled_program_changes) {
      const date = format(new Date(patientData.scheduled_program_changes.change_at), 'dd.MM.yyyy');
      title = `Смена программы уже запланирована на  ${date}`;
    }
    if (isAddingForbiddenByDates) {
      title = 'Нет доступных дат для смены программы';
    }
    return title;
  }, [isAddingForbiddenByDates, patientData?.scheduled_program_changes, patientData?.type]);

  const handlePolicyButtonClick = async (): Promise<void> => {
    // по хорошему, надо это все вынести, но тут черти что со структурой, поэтому пока решил тут оставить
    await handleRefreshToken();
    const token = localStorage.getItem('token');

    const response = await fetch(patientData?.medPolicy ?? '', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: `${HEADER_ACCEPT_HR}`,
        'Content-Type': 'application/pdf',
      },
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  if (isErrorPatient) {
    return <p>{`Не удалось загрузить страницу. ${isErrorPatient}`}</p>;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        disableEnforceFocus={isDetachPatientModal}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            minWidth: '800px',
            overflow: 'auto',
            padding: '20px 24px 24px',
            borderRadius: '24px',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ width: '100%' }}>
            <Box>
              <Text as="h3" variant="heading-3" color={{ surface: 'light', variant: 'primary' }}>
                {patientData?.full_name || 'Данные не заполнены'}
              </Text>

              <div className="size-12" />

              <Typography variant="subtitle1" sx={() => modalTypographyColor('rgba(62, 65, 77, 0.6)')}>
                {`${patientData?.company || null} / (ID: ${patientData?.id || 'Данные не заполнены'}) `}
              </Typography>

              <div className="size-8" />

              {patientData?.program_period && (
                <Typography variant="subtitle1" sx={() => modalTypographyColor('rgba(62, 65, 77, 0.6)')}>
                  {(() => {
                    const periodParts = patientData.program_period.split(' - ');
                    if (periodParts.length === 2) {
                      return `Действие с ${periodParts[0]} по ${periodParts[1]}`;
                    }
                    return `Действие ${patientData.program_period}`;
                  })()}
                </Typography>
              )}
            </Box>

            <div className="size-8" />

            <Stack direction="row" gap="16px" alignItems="center">
              <Typography variant="subtitle1" sx={() => modalTypographyColor('#ff6d6d')}>
                {(() => {
                  if (isPatientTypeProposalDetach) {
                    const date = format(new Date(proposalDetachmentActionData?.data?.detached_at), 'dd.MM.yyyy');
                    return `Пациент подан на открепление с ${date}`;
                  }
                  if (patientData?.scheduled_program_changes) {
                    const date = format(new Date(patientData.scheduled_program_changes.change_at), 'dd.MM.yyyy');
                    return `Программа будет сменена на ${patientData.scheduled_program_changes.title} с ${date}`;
                  }
                  if (patientData?.type !== 'Завершенный') {
                    return `${patientData?.type || 'Данные не заполнены'} c ${
                      patientData?.start_date || 'Данные не заполнены'
                    }`;
                  }
                  if (patientData?.type === 'Завершенный') {
                    return `${patientData?.type || 'Данные не заполнены'} c ${
                      patientData?.end_date || 'Данные не заполнены'
                    }`;
                  }
                  return `${patientData?.type || 'Данные не заполнены'} c ${
                    patientData?.start_date || 'Данные не заполнены'
                  }`;
                })()}
              </Typography>
              <Box
                sx={{
                  color: themeColor,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={() => modalTypographyColor('#ff6d6d')}
                  onClick={() => {
                    refetchCompanyActions();
                    handleClose();
                    setHistoryActionsModal(true);
                  }}
                >
                  История операций
                </Typography>
              </Box>
            </Stack>
          </Box>
          <IconButton aria-label="close modal" sx={{ width: '40px', height: '40px' }} onClick={handleClose}>
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            marginTop: '12px',
            width: '624px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography
              variant="subtitle2"
              sx={{
                margin: '0 4px 0 0',
                color: 'rgba(62, 65, 77, 0.6)',
              }}
            >
              Статус приветственного письма:&#32;
            </Typography>

            <Typography variant="subtitle1" sx={{ margin: '0 18px 0 0', color: '#3e414d' }}>
              {patientData?.welcome_newsletter_status || 'Данные не заполнены'}
            </Typography>
          </Box>
          {isAccessAllowed(patientData?.companyUuid, 'HR_slots_welcomemail', accessList, defaultPermissions) &&
            isAccessAllowed(patientData?.companyUuid, 'HR_slots', accessList, defaultPermissions) && (
              <Button
                variant="pseudo"
                size="s"
                disabled={patientData ? patientData.type !== 'Активный' : !!patientData}
                leftElement={<EmailOutlinedIcon sx={{ width: '16px' }} />}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={handleSendWelcomeLetter}
              >
                Отправить еще раз
              </Button>
            )}
        </Box>

        {searchRelativePatientsData?.patients?.length > 0 && (
          <Box
            sx={{
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '14px',
              color: theme.palette.primary.main,
            }}
          >
            Имеет родственные связи
          </Box>
        )}

        <Box sx={{ maxHeight: '600px', overflowX: 'hidden', overflowY: 'scroll' }}>
          <Box sx={{ marginTop: '16px', marginBottom: '16px' }}>
            <Divider />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ margin: '0 0 12px', color: '#3e414d' }}>
              Персональная информация
            </Typography>

            <Box sx={modalBlockRowStyle}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Гражданство
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.nationality || 'Данные не заполнены'}
                </Typography>
              </Box>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Адрес фактического проживания
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.address || 'Данные не заполнены'}
                </Typography>
              </Box>
            </Box>

            <Box sx={modalBlockRowStyle}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Дата рождения
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.birth_date || 'Данные не заполнены'}
                </Typography>
              </Box>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Пол
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.gender || 'Данные не заполнены'}
                </Typography>
              </Box>
            </Box>

            <Box sx={modalBlockRowStyle}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Телефон
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.phone || 'Данные не заполнены'}
                </Typography>
              </Box>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  E-mail
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.email || 'Данные не заполнены'}
                </Typography>
              </Box>
            </Box>

            <Box sx={modalBlockRowStyle}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Паспорт
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.passport_number || 'Данные не заполнены'}
                </Typography>
              </Box>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Дата выдачи
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.passport_date || 'Данные не заполнены'}
                </Typography>
              </Box>
            </Box>

            <Box sx={modalBlockRowStyle}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Кем выдан
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.passport_given || 'Данные не заполнены'}
                </Typography>
              </Box>
            </Box>

            <Box sx={modalBlockRowStyle}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Должность
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.position || 'Данные не заполнены'}
                </Typography>
              </Box>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Табельный номер
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.number || 'Данные не заполнены'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ marginTop: '16px', marginBottom: '16px' }}>
            <Divider />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ margin: '0 0 12px', color: '#3e414d' }}>
              Программа
            </Typography>

            <Box sx={modalBlockRowStyle}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Наименование
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.program_title || 'Данные не заполнены'}
                </Typography>
              </Box>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Период обслуживания
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.program_period || 'Данные не заполнены'}
                </Typography>
              </Box>
            </Box>

            <Box sx={modalBlockRowStyle}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Объем
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.service_types?.map((item) => item.title).join(' ,') || 'Данные не заполнены'}
                </Typography>
              </Box>
            </Box>

            <Box sx={modalBlockRowStyle}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Номер полиса страховщика
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.policy_number || 'Данные не заполнены'}
                </Typography>
              </Box>
            </Box>

            <Box sx={modalBlockRowStyle}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                  Доп. информация
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#3e414d' }}>
                  {patientData?.additional_info || 'Данные не заполнены'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {searchRelativePatientsData?.patients?.length > 0 && (
            <Box sx={{ marginTop: '40px' }}>
              <Typography variant="h4" sx={{ margin: '0 0 12px', color: '#3e414d' }}>
                Родственники
              </Typography>
              <Box>
                <Stack
                  sx={{
                    flexDirection: 'row',
                    width: '100%',
                    justifyContent: 'space-between',
                    marginBottom: '16px',
                  }}
                >
                  <Stack
                    sx={{
                      minWidth: '33%',
                    }}
                  >
                    <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                      Фио
                    </Typography>
                  </Stack>
                  <Stack
                    sx={{
                      minWidth: '33%',
                    }}
                  >
                    <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                      Дата рождения
                    </Typography>
                  </Stack>
                  <Stack
                    sx={{
                      minWidth: '33%',
                    }}
                  >
                    <Typography variant="h5" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>
                      Родственная связь
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {searchRelativePatientsData?.patients?.map((patient: SearchRelativePatient) => (
                    <Stack
                      sx={{
                        flexDirection: 'row',
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                      key={patient.id}
                    >
                      <Typography
                        variant="subtitle1"
                        onClick={() => {
                          setSlotRelative(patient);
                          setSlotsModalOpen(true);
                          handleClose();
                        }}
                        sx={{
                          color: theme.palette.primary.main,
                          width: '33%',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        {patient?.fullName}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ color: '#3e414d', width: '33%' }}>
                        {getFormattedDate(patient?.birthDate, 'dd.MM.yyyy')}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ color: '#3e414d', width: '33%' }}>
                        {patient?.relativeType}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Box>
          )}

          {isAccessAllowed(patientData?.companyUuid, 'HR_slots_docs', accessList, defaultPermissions) &&
            isAccessAllowed(patientData?.companyUuid, 'HR_slots', accessList, defaultPermissions) && (
              <>
                <Box sx={{ marginTop: '16px', marginBottom: '16px' }}>
                  <Divider />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ margin: '0 0 12px', color: '#3e414d' }}>
                    Документы
                  </Typography>

                  <Box
                    className="modal-info-staff__block-row modal-info-staff__block-row_type_btns"
                    sx={{
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      gap: '16px',
                    }}
                  >
                    <Button
                      variant="secondary"
                      size="s"
                      leftElement={<FavoriteOutlinedIcon sx={{ width: '16px', color: 'rgba(92, 119, 125, 1)' }} />}
                      onClick={handlePolicyButtonClick}
                    >
                      {`Полис ${getCompanyName()}`}
                    </Button>
                    <Button
                      variant="secondary"
                      size="s"
                      leftElement={<LuchiIcon sx={{ width: '16px' }} />}
                      onClick={() => {
                        window.open(patientData?.medProgram);
                      }}
                    >
                      Мед. программа
                    </Button>
                    {patientData?.vzrPolicy !== null && (
                      <Button
                        variant="secondary"
                        size="s"
                        leftElement={<FileCopyIcon sx={{ width: '16px', color: 'rgba(92, 119, 125, 1)' }} />}
                        onClick={() => window.open(patientData?.vzrPolicy)}
                      >
                        Полис ВЗР
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="s"
                      leftElement={<ExternalLinkIcon sx={{ width: '16px', color: 'rgba(92, 119, 125, 1)' }} />}
                      onClick={handleLoadClinicsList}
                    >
                      Список клиник
                    </Button>
                  </Box>
                </Box>
              </>
            )}
        </Box>

        <Box sx={modalBlockFooterStyle}>
          {isPatientTypeSlot &&
            !isPatientTypeProposalDetach &&
            isAccessAllowed(
              patientData?.companyUuid,
              'HR_slots_action_changeprogram',
              accessList,
              defaultPermissions
            ) &&
            isAccessAllowed(patientData?.companyUuid, 'HR_slots', accessList, defaultPermissions) && (
              <TooltipActionsInfo placement="left" title={tooltipTitle}>
                <Box>
                  <Button
                    variant="secondary"
                    onClick={() => setIsChangeProgramModal(true)}
                    disabled={
                      patientData?.scheduled_program_changes !== null ||
                      patientData?.type === 'Завершенный' ||
                      isAddingForbiddenByDates
                    }
                  >
                    Редактировать программу
                  </Button>
                </Box>
              </TooltipActionsInfo>
            )}

          {isPatientTypeSlot &&
            !isPatientTypeProposalDetach &&
            isAccessAllowed(patientData?.companyUuid, 'HR_slots_action_attach', accessList, defaultPermissions) &&
            isAccessAllowed(patientData?.companyUuid, 'HR_slots', accessList, defaultPermissions) && (
              <TooltipActionsInfo placement="left" title={getDetachTooltipTitle(isDetachingForbiddenByDates) || ''}>
                <Box component="span">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setDetachPatientModal(true);
                    }}
                    disabled={
                      (patientData ? patientData.type === 'Завершенный' : !!patientData) || isDetachingForbiddenByDates
                    }
                  >
                    Открепить
                  </Button>
                </Box>
              </TooltipActionsInfo>
            )}

          {!isPatientTypeProposalDetach &&
            isAccessAllowed(patientData?.companyUuid, 'HR_slots', accessList, defaultPermissions) &&
            isAccessAllowed(patientData?.companyUuid, 'HR_slots_action_edit', accessList, defaultPermissions) && (
              <TooltipActionsInfo
                placement="left"
                title={
                  patientData?.type === 'Завершенный' ? 'Нельзя редактировать пациентов с завершенным периодом' : ''
                }
              >
                <Box component="span">
                  <Button
                    variant="secondary"
                    disabled={patientData ? patientData.type === 'Завершенный' : !!patientData}
                    onClick={handleEditButtonClick}
                  >
                    Редактировать
                  </Button>
                </Box>
              </TooltipActionsInfo>
            )}

          {!isPatientTypeSlot && !isPatientTypeProposalDetach && (
            <Button
              variant="pseudo"
              onClick={() => setEarlyAttachWarning(true)}
            >
              {
                <>
                  Активировать сразу{' '}
                  <TooltipActionsWarning
                    placement="top"
                    title={
                      <>
                        <Box
                          component="p"
                          sx={{
                            fontSize: '10px',
                            lineHeight: '12px',
                            letterSpacing: '0.02em',
                            textAlign: 'left',
                          }}
                        >
                          Если вам необходимо получить документы по прикреплению сразу, вы можете принудительно
                          активировать прикрепление
                        </Box>
                        <Box
                          component="p"
                          sx={{
                            fontSize: '10px',
                            lineHeight: '12px',
                            letterSpacing: '0.02em',
                            textAlign: 'left',
                          }}
                        >
                          Обратите внимание, что отменить действие или скорректировать данные будет невозможно
                        </Box>
                      </>
                    }
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{
                        color: themeColor,
                        height: '16px',
                        width: '16px',
                        transform: 'translateY(-60%) translateX(10%)',
                      }}
                    />
                  </TooltipActionsWarning>
                </>
              }
            </Button>
          )}

          {!isPatientTypeSlot && !isPatientTypeProposalDetach && (
            <Button
              variant="pseudo"
              onClick={() => setCancelAttachWarning(true)}
            >
                <>
                  Отменить{' '}
                  <TooltipActionsWarning
                    placement="top"
                    title={
                      <Box
                        component="p"
                        sx={{
                          fontSize: '10px',
                          lineHeight: '12px',
                          letterSpacing: '0.02em',
                          textAlign: 'left',
                        }}
                      >
                        Действие будет отменено без отображения в документах
                      </Box>
                    }
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{
                        color: themeColor,
                        height: '16px',
                        width: '16px',
                        transform: 'translateY(-60%) translateX(10%)',
                      }}
                    />
                  </TooltipActionsWarning>
                </>
            </Button>
          )}
          {isPatientTypeProposalDetach && (
            <>
              <Button
                variant="pseudo"
                onClick={() => handleCancelDetachment(proposalDetachmentActionData?.data?.base_action?.uuid)}
              >
                  <>
                    Восстановить прикрепление
                    <TooltipActionsWarning
                      placement="top"
                      title={
                        <>
                          {' '}
                          <Box
                            component="p"
                            sx={{
                              fontSize: '10px',
                              lineHeight: '12px',
                              letterSpacing: '0.02em',
                              textAlign: 'left',
                            }}
                          >
                            Пациент уже подан на открепление с{' '}
                            {format(new Date(proposalDetachmentActionData?.data?.detached_at), 'dd.MM.yyyy')}{' '}
                          </Box>
                          <Box
                            component="p"
                            sx={{
                              fontSize: '10px',
                              lineHeight: '12px',
                              letterSpacing: '0.02em',
                              textAlign: 'left',
                            }}
                          >
                            Обратите внимание, что данное действие отменит открепление без отображения в документах.
                          </Box>
                        </>
                      }
                    >
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{
                          color: themeColor,
                          height: '16px',
                          width: '16px',
                          transform: 'translateY(-60%) translateX(10%)',
                        }}
                      />
                    </TooltipActionsWarning>
                  </>
              </Button>
              <Button
                variant="pseudo"
                onClick={() => {
                  setDetachPatientModal(true);
                  setEditDetachModal(true);
                }}
              >
                  <>
                    Изменить детали открепления
                    <TooltipActionsWarning
                      placement="top"
                      title={
                        <Box
                          component="p"
                          sx={{
                            fontSize: '10px',
                            lineHeight: '12px',
                            letterSpacing: '0.02em',
                            textAlign: 'left',
                          }}
                        >
                          Обратите внимание, что пациент уже подан на открепление c{' '}
                          {format(new Date(proposalDetachmentActionData?.data?.detached_at), 'dd.MM.yyyy')}, повторное
                          действие скорректирует дату откреплениям без отображения в документах
                        </Box>
                      }
                    >
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{
                          color: themeColor,
                          height: '16px',
                          width: '16px',
                          transform: 'translateY(-60%) translateX(10%)',
                        }}
                      />
                    </TooltipActionsWarning>
                  </>
              </Button>
            </>
          )}

          <Button variant="promo" onClick={handleClose}>
            Закрыть
          </Button>
        </Box>
      </Dialog>

      {!!patientData && (
        <DetachPatientModal
          open={isDetachPatientModal}
          isEditDetachModal={isEditDetachModal}
          massDetach={false}
          patientData={patientData}
          detachDate={proposalDetachmentActionData?.data?.detached_at}
          onSubmit={analyticsTracker.trackRemovalPatientCardAttempt}
          handleClose={() => {
            setDetachPatientModal(false);
            setEditDetachModal(false);
            analyticsTracker.trackRemovalPatientCardSuccess();
          }}
        />
      )}

      {!!patientData && (
        <HistoryActionsModal
          open={isHistoryActionsModal}
          patientData={patientData}
          historyActions={CompanyActionsData || []}
          isLoadingCompanyActions={isLoadingCompanyActions}
          hasNextPageCompanyActions={hasNextPageCompanyActions}
          isFetchingNextPageCompanyActions={isFetchingNextPageCompanyActions}
          fetchNextPageCompanyActions={fetchNextPageCompanyActions}
          startDate={startDate}
          handleStartDate={setStartDate}
          endDate={endDate}
          handleEndDate={setEndDate}
          eventType={eventType}
          handleEventType={setEventType}
          orderingTableHistory={orderingTableHistory}
          handleOrderingTableHistory={setOrderingTableHistory}
          handleClose={() => setHistoryActionsModal(false)}
        />
      )}

      {/* монтируем только открытой: закрытая модалка тянет список программ в фоне */}
      {!!patientData && isEditPatientModal && (
        <EditPatientModal
          isAddingForbiddenByDates={isAddingForbiddenByDates}
          selectedCompanies={selectedCompanies}
          periodByAttachOrDetach={periodStaffAction}
          patientData={patientData}
          open={isEditPatientModal}
          handleClose={() => setEditPatientModal(false)}
          refetchPatient={refetchPatient}
        />
      )}
      <ChangeProgramModal
        open={isChangeProgramModal}
        handleClose={() => setIsChangeProgramModal(false)}
        periodByAttachOrDetach={periodStaffAction}
        parentClose={handleClose}
        patientData={patientData}
        programs={programs}
        patientProgram={patientProgram}
        refetchPatient={refetchPatient}
      />
      {slotsModalOpen && (
        <RelativeSlotsModal
          open={slotsModalOpen}
          handleClose={() => setSlotsModalOpen(false)}
          data={slotRelative}
          accessList={accessList}
          rolesData={rolesData}
          refetchSearchPatients={refetchSearchPatients}
          defaultPermissions={defaultPermissions}
        />
      )}
      <EarlyAttachWarning
        open={isEarlyAttachWarning}
        handleClose={() => setEarlyAttachWarning(false)}
        handleClickBtn={() => handleForceAddPatient(patientData?.uuid)}
      />
      <CancelAttachWarning
        open={isCancelAttachWarning}
        handleClose={() => setCancelAttachWarning(false)}
        handleClickBtn={() => {
          handleCancelPatientAttach(patientData?.uuid);
          refetchSearchPatients();
          handleClose();
        }}
      />
      {/* монтируем только открытой: закрытая модалка тянет список программ в фоне */}
      {patientData && searchRelativePatientsData && isEditProposalModal && (
        <EditProposalModal
          patientData={patientData}
          isAddingForbiddenByDates={isAddingForbiddenByDates}
          celeryType="slots"
          open={isEditProposalModal}
          companiesHR={selectedCompanies}
          selectedCompanies={selectedCompanies}
          periodInMainFilter={mainFilterPeriodStaff}
          handleClose={() => setEditProposalModal(false)}
          handleTaskIdAttach={() => null}
          accessList={accessList}
          defaultPermissions={defaultPermissions}
          relativeData={searchRelativePatientsData}
          handleCancelPatientAttach={handleCancelPatientAttach}
          searchRelativePatientsData={searchRelativePatientsData}
          handleCloseParent={handleClose}
        />
      )}
    </>
  );
};
